import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRightArrowLeft,
  faCircleQuestion,
  faCirclePlus,
  faCommentDots,
  faFileInvoice,
  faFlagCheckered,
  faGavel,
  faLock,
  faReply,
  faScrewdriverWrench,
  faShareNodes,
  faTags,
} from '@fortawesome/free-solid-svg-icons';
import { ENTRY_TYPE_LABELS } from '@/features/inspection/modification-state-rules';
import type {
  ModificationEntryType,
  ModificationOwner,
} from '@/services/inspection-modification.service';
import type { ThreadTimelineEntry } from '@/services/request-thread.service';
import { RevisionLine } from './ThreadContextCards';

/**
 * The Request Thread timeline — the append-only record of one customer request.
 *
 * ── IT RENDERS WHAT IT IS GIVEN. ALL OF IT. IN ORDER. ───────────────────────
 * There is no `.filter()` in this file and there must never be one. Visibility
 * is decided in the SQL `where` of the composed read: an entry this caller may
 * not see was never selected, never serialised and never arrived. A client-side
 * filter here would not add safety — it would only create a second, weaker rule
 * that could disagree with the first, and would invite someone to conclude the
 * backend's exclusion was belt-and-braces rather than the boundary.
 *
 * The same applies to order. `createdAt ASC` is settled by the query; re-sorting
 * here could only move an entry away from where the record puts it.
 *
 * ── EVIDENCE, NOT A SECOND WORKFLOW ENGINE ──────────────────────────────────
 * These rows are what HAPPENED. Nothing here derives the current state, the
 * owner, who is waited on, or what anyone may do — all of which arrive already
 * decided on the thread and are rendered by the header and the action region.
 * No entry is merged, deduplicated or dropped because it resembles its
 * neighbour: two similar events an hour apart are two events.
 *
 * ── SEMANTICS SURVIVE ───────────────────────────────────────────────────────
 * Twelve entry types, twelve meanings. A triage is not a chat message and an
 * internal note is not a customer reply, so each keeps its own label (from the
 * shared `ENTRY_TYPE_LABELS` vocabulary), icon and tone. Flattening them into a
 * generic feed is exactly what would make the record unreadable as evidence.
 */

/** What kind of event this is — the grouping the reader actually perceives. */
type EntryTone = 'request' | 'customer' | 'staff' | 'internal' | 'closure';

interface EntryStyle {
  icon: IconDefinition;
  tone: EntryTone;
}

const ENTRY_STYLE: Record<ModificationEntryType, EntryStyle> = {
  // The request itself, and everything the customer later added to it. One
  // strand: this is the ask, as it stands.
  REQUESTED: { icon: faCommentDots, tone: 'request' },
  ADDED: { icon: faCirclePlus, tone: 'request' },
  // The customer answering or deciding — their voice, but not a new ask.
  CUSTOMER_REPLIED: { icon: faReply, tone: 'customer' },
  CUSTOMER_DECIDED: { icon: faGavel, tone: 'customer' },
  // Staff workflow events: who routed it where, and what they answered.
  TRIAGED: { icon: faTags, tone: 'staff' },
  REFERRED_TO_OPS: { icon: faArrowRightArrowLeft, tone: 'staff' },
  OPS_VERDICT: { icon: faScrewdriverWrench, tone: 'staff' },
  CLARIFICATION_REQUESTED: { icon: faCircleQuestion, tone: 'staff' },
  NOTE_PUBLISHED: { icon: faShareNodes, tone: 'staff' },
  REVISION_SENT: { icon: faFileInvoice, tone: 'staff' },
  // Never the customer's, whatever the visibility column happens to say.
  INTERNAL_NOTE: { icon: faLock, tone: 'internal' },
  CLOSED: { icon: faFlagCheckered, tone: 'closure' },
};

const TONE_CLASS: Record<EntryTone, string> = {
  request: 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
  customer: 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
  staff: 'bg-muted text-muted-foreground',
  internal: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  closure: 'bg-green-500/10 text-green-700 dark:text-green-300',
};

const OWNER_LABEL: Record<ModificationOwner, string> = {
  COMMERCIAL: 'Commercial',
  OPERATIONS: 'Operations',
};

/** The two entry types that carry the customer's ASK. Presentation only — it
 * groups a strand visually and removes nothing. */
const REQUEST_CONTENT: ReadonlySet<string> = new Set(['REQUESTED', 'ADDED']);

/**
 * Who recorded this, said honestly.
 *
 * Two shapes in the real record make the naive answer wrong:
 *
 *   · The backfilled genesis entry carries NO actor id at all — neither user nor
 *     contact. `authorKind` still says CUSTOMER, which is the truthful
 *     attribution, so that is what is shown. No name is invented.
 *   · An `ADDED` written through the staff-proxy route is `authorKind: STAFF`
 *     while its BODY is the customer's own words. Calling that "Demo Commercial
 *     added to the request" would credit the wrong person with the ask. The
 *     entry TYPE carries the authorship — the label already says the customer
 *     added to the request — and the staff member is named as who wrote it down.
 */
function attribution(entry: ThreadTimelineEntry): string {
  const name = entry.actor.name;
  if (entry.authorKind === 'SYSTEM') return 'System';
  if (entry.authorKind === 'CUSTOMER') return name ? `Customer · ${name}` : 'Customer';
  // STAFF.
  if (!name) return 'Service team';
  return REQUEST_CONTENT.has(entry.entryType) ? `recorded by ${name}` : name;
}

/** The ownership hop this entry caused, when it caused one. All four transition
 * columns null means "not a hop" — the D-112 convention — and nothing is shown
 * rather than a department guessed from the actor's current role. */
function hopLabel(entry: ThreadTimelineEntry): string | null {
  if (entry.fromOwner && entry.toOwner && entry.fromOwner !== entry.toOwner) {
    return `${OWNER_LABEL[entry.fromOwner]} → ${OWNER_LABEL[entry.toOwner]}`;
  }
  if (entry.department) return `With ${OWNER_LABEL[entry.department]}`;
  return null;
}

function Entry({
  entry,
  continuesRequest,
}: {
  entry: ThreadTimelineEntry;
  /** True when the entry above is also request content, so the two read as one
   * strand. Purely visual — no entry is merged and none is removed. */
  continuesRequest: boolean;
}) {
  const style = ENTRY_STYLE[entry.entryType] ?? { icon: faCommentDots, tone: 'staff' as const };
  const label = ENTRY_TYPE_LABELS[entry.entryType] ?? entry.entryType;
  const hop = hopLabel(entry);
  const at = new Date(entry.createdAt);

  return (
    <li
      className="relative flex gap-3 pb-5 last:pb-0"
      data-testid="thread-timeline-entry"
      data-entry-type={entry.entryType}
      data-visibility={entry.visibility}
      data-request-group={continuesRequest ? 'continues' : undefined}
    >
      {/* The rail. Decorative: the <ol> already carries the chronology. */}
      <span
        aria-hidden="true"
        className="absolute bottom-0 left-[13px] top-7 w-px bg-border last:hidden"
      />
      <span
        aria-hidden="true"
        className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${TONE_CLASS[style.tone]}`}
      >
        <FontAwesomeIcon icon={style.icon} className="h-3 w-3" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h3 className="text-sm font-medium text-foreground">{label}</h3>
          {entry.visibility === 'INTERNAL' && (
            // Text AND an icon, never colour alone — staff must be able to tell
            // at a glance what the customer can also read.
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
              <FontAwesomeIcon icon={faLock} className="h-2.5 w-2.5" aria-hidden="true" />
              Internal
            </span>
          )}
          <time
            dateTime={entry.createdAt}
            title={at.toLocaleString()}
            className="ml-auto shrink-0 text-xs text-muted-foreground"
          >
            {at.toLocaleString()}
          </time>
        </div>

        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="break-words">{attribution(entry)}</span>
          {hop && (
            <>
              <span aria-hidden="true">·</span>
              <span data-testid="thread-entry-hop">{hop}</span>
            </>
          )}
        </p>

        {/* VERBATIM. `whitespace-pre-wrap` keeps the author's own spacing and
            line breaks; `break-words` keeps a long unbroken string inside the
            column instead of widening the page. A null body renders nothing at
            all — the event label is the record, and inventing a sentence for a
            bodiless TRIAGED row would be fabricating evidence. */}
        {entry.body !== null && entry.body !== '' && (
          <p
            data-testid="thread-entry-body"
            className={`mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground ${
              REQUEST_CONTENT.has(entry.entryType)
                ? 'rounded-lg border border-sky-500/20 bg-sky-500/[0.04] px-3 py-2'
                : ''
            }`}
          >
            {entry.body}
          </p>
        )}

        {/* E-iv — the revision marker. Rendered ONLY when the server attached
            one; this file decides nothing about which entry deserves it and
            compares nothing to work it out. Deliberately subordinate to the
            event content above it: a quiet identity line, not a card, so the
            entry itself remains the record and the marker only says which
            quotation revision that entry settled. */}
        {entry.revision && (
          <p
            data-testid="thread-entry-revision"
            className="mt-1.5 rounded-lg border border-border/60 bg-muted/30 px-3 py-1.5 text-sm text-foreground"
          >
            <RevisionLine revision={entry.revision} />
          </p>
        )}
      </div>
    </li>
  );
}

export function ThreadTimeline({ entries }: { entries: ThreadTimelineEntry[] }) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="thread-timeline-empty">
        Nothing has been recorded on this request yet.
      </p>
    );
  }

  return (
    <ol className="space-y-0" data-testid="thread-timeline">
      {entries.map((entry, i) => (
        <Entry
          key={entry.id}
          entry={entry}
          // Adjacency only: an addition that follows the request directly reads
          // as part of it. Computed from position, never by reordering or by
          // pulling entries out of the sequence the server returned.
          continuesRequest={
            REQUEST_CONTENT.has(entry.entryType) &&
            i > 0 &&
            REQUEST_CONTENT.has(entries[i - 1].entryType)
          }
        />
      ))}
    </ol>
  );
}
